import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { type ReactNode, useId } from 'react';
import { useDelayedFlag } from '#client/hooks/useDelayedFlag';
import { cn } from '#client/lib/cn';
import { Skeleton } from './Skeleton';
import {
  type TableSort,
  ariaSortFor,
  cellClassName,
  sortIconFor,
  tableStatusText,
} from './table.logic';

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** Cell renderer. A function, so cells are typed rather than string-keyed. */
  render: (row: T) => ReactNode;
  align?: 'start' | 'center' | 'end';
  width?: string;
  /**
   * Pin this column to the inline-start edge during horizontal scroll.
   *
   * At most one, and it should be the row's identifier — a PIN stays visible
   * while an operator scrolls sideways through a wide worklist, which is the
   * only reason they can tell which row they are reading.
   */
  frozen?: boolean;
  /** Marks the column sortable. Inert until the API accepts a sort parameter. */
  sortable?: boolean;
  /** Placeholder width used while loading, so skeleton rows match the real ones. */
  skeletonWidth?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  loading?: boolean | undefined;
  /** How many skeleton rows to draw while loading. Pass the page size. */
  skeletonRows?: number | undefined;
  emptyState?: ReactNode | undefined;
  /** Rendered in a trailing actions column. */
  rowActions?: ((row: T) => ReactNode) | undefined;
  onRowClick?: ((row: T) => void) | undefined;
  /** Accessible name for the table. Strongly recommended. */
  caption?: string | undefined;
  /** Keeps the column header visible while the body scrolls. */
  stickyHeader?: boolean | undefined;
  sort?: TableSort | undefined;
  onSortChange?: ((key: string) => void) | undefined;
  className?: string | undefined;
}

/**
 * The data table.
 *
 * ## Density
 *
 * Deliberately not a prop. Row height comes from `--row-h`, set on `<html>` by
 * the user's persisted preference, so every table in the app changes together.
 * An operator who wants dense rows wants them everywhere; per-table density
 * would read as inconsistency.
 *
 * ## Keyboard-operable rows
 *
 * `onRowClick` on a bare `<tr>` alone would be invisible to the keyboard and
 * to screen readers — the rows would be mouse-only. The first cell's content
 * is wrapped in a real `<button>` stretched over the row via an
 * absolutely-positioned `::after`, so the row is reachable by Tab, activatable
 * by Enter and Space, and announced as a control. The `<tr>` keeps its
 * `onClick` purely as a mouse convenience.
 *
 * ## Sorting
 *
 * Scaffolded, not shipped. `api.list` sends only `{page, limit, search}` and
 * the server has no sort parameter, so client-sorting the twenty rows of one
 * page while four hundred records exist would be a lying feature. The props
 * and `aria-sort` markup land now; headers stay inert until `onSortChange` is
 * passed, which awaits the API.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  skeletonRows = 8,
  emptyState = 'No records found',
  rowActions,
  onRowClick,
  caption,
  stickyHeader = true,
  sort,
  onSortChange,
  className,
}: DataTableProps<T>) {
  const statusId = useId();

  // Below ~200ms a placeholder reads as a flicker, so nothing is shown at all.
  const showSkeleton = useDelayedFlag(loading, 250);
  const hasActions = rowActions !== undefined;

  return (
    <>
      <div
        className={cn('c-table__scroll', stickyHeader && 'c-table__scroll--sticky', className)}
        // A horizontally scrollable region must be focusable, or a keyboard
        // user cannot scroll it to reach the columns on the right.
        tabIndex={0}
        role="region"
        aria-label={caption ?? 'Data table'}
      >
        <table className="c-table">
          {caption && <caption className="sr-only">{caption}</caption>}

          <thead>
            <tr>
              {columns.map((column) => (
                <HeaderCell
                  key={column.key}
                  column={column}
                  sort={sort}
                  onSortChange={onSortChange}
                />
              ))}

              {hasActions && (
                <th scope="col" className="c-table__th c-table__cell--end">
                  Actions
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {loading && showSkeleton && (
              <SkeletonRows columns={columns} count={skeletonRows} hasActions={hasActions} />
            )}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={columns.length + (hasActions ? 1 : 0)} className="c-table__state">
                  {emptyState}
                </td>
              </tr>
            )}

            {!loading &&
              rows.map((row) => (
                <BodyRow
                  key={rowKey(row)}
                  row={row}
                  columns={columns}
                  actions={{ onRowClick, rowActions }}
                />
              ))}
          </tbody>
        </table>
      </div>

      {/*
        One polite live region for the table's async state, so a screen-reader
        user is told the results changed. The skeleton rows themselves are
        aria-hidden — announcing thirty placeholder cells would be noise.
      */}
      <p id={statusId} className="sr-only" role="status">
        {tableStatusText(loading, rows.length)}
      </p>
    </>
  );
}

function HeaderCell<T>({
  column,
  sort,
  onSortChange,
}: {
  column: Column<T>;
  sort: TableSort | undefined;
  onSortChange: ((key: string) => void) | undefined;
}) {
  const interactive = column.sortable && onSortChange;

  return (
    <th
      scope="col"
      style={column.width ? { width: column.width } : undefined}
      className={cn('c-table__th', cellClassName(column))}
      aria-sort={ariaSortFor(column, sort)}
    >
      {interactive ? (
        <button type="button" className="c-table__sort" onClick={() => onSortChange(column.key)}>
          {column.header}
          <SortIcon state={sortIconFor(column.key, sort)} />
        </button>
      ) : (
        column.header
      )}
    </th>
  );
}

function SortIcon({ state }: { state: ReturnType<typeof sortIconFor> }) {
  if (state === 'ascending') return <ArrowUp aria-hidden="true" />;
  if (state === 'descending') return <ArrowDown aria-hidden="true" />;
  return <ArrowUpDown aria-hidden="true" />;
}

/**
 * Skeleton rows mirror the real column layout — same count, same widths — so
 * the table does not reflow when the data lands.
 */
function SkeletonRows<T>({
  columns,
  count,
  hasActions,
}: {
  columns: Column<T>[];
  count: number;
  hasActions: boolean;
}) {
  return Array.from({ length: count }, (_, index) => (
    <tr key={`skeleton-${index}`} className="c-table__row">
      {columns.map((column) => (
        <td key={column.key} className={cn('c-table__td', cellClassName(column))}>
          <Skeleton width={column.skeletonWidth ?? '70%'} />
        </td>
      ))}
      {hasActions && (
        <td className="c-table__td c-table__cell--end">
          <Skeleton width="3rem" />
        </td>
      )}
    </tr>
  ));
}

interface RowHandlers<T> {
  onRowClick?: ((row: T) => void) | undefined;
  rowActions?: ((row: T) => ReactNode) | undefined;
}

function BodyRow<T>({
  row,
  columns,
  actions: { onRowClick, rowActions },
}: {
  row: T;
  columns: Column<T>[];
  actions: RowHandlers<T>;
}) {
  return (
    <tr
      className={cn('c-table__row', onRowClick && 'c-table__row--clickable')}
      onClick={onRowClick ? () => onRowClick(row) : undefined}
    >
      {columns.map((column, index) => (
        <td key={column.key} className={cn('c-table__td', cellClassName(column))}>
          {onRowClick && index === 0 ? (
            <button
              type="button"
              className="c-table__row-target"
              onClick={(event) => {
                // The <tr> also handles the click; without this the
                // handler would fire twice.
                event.stopPropagation();
                onRowClick(row);
              }}
            >
              {column.render(row)}
            </button>
          ) : (
            column.render(row)
          )}
        </td>
      ))}

      {rowActions && (
        <td
          className="c-table__td c-table__cell--end c-table__actions"
          // Keep action buttons from also triggering the row click.
          onClick={(event) => event.stopPropagation()}
        >
          {rowActions(row)}
        </td>
      )}
    </tr>
  );
}
